import * as React from "react";

/**
 * The mechanics behind anything that opens a floating panel next to a trigger:
 * `Menu` and `FilterSelect` today, and anything similar later.
 *
 * It is a hook rather than a shared component because the two differ in every
 * way that shows on screen - one is a list of actions, the other a list of
 * values with a selected state - and in nothing that is hard to get right. The
 * hard parts are all here, and all of them have a specific failure behind them:
 *
 * **Escape and outside-click are bound on the document.** A handler on the panel
 * only fires while focus is inside it, which stops being true the moment the
 * user tabs away, leaving an open panel over the page with no way to dismiss it.
 *
 * **`pointerdown`, not `click`, for the outside dismiss.** A click on some other
 * button fires after that button's own handler, so a `click` listener leaves the
 * panel open on top of whatever just happened.
 *
 * **Focus returns to the trigger on close.** Otherwise Escape drops focus onto
 * `<body>` and a keyboard user restarts their tab journey from the top.
 *
 * **The panel is portalled to `<body>` and positioned `fixed`.** This is the one
 * that stays invisible until it bites: these panels open inside `Table`, whose
 * wrapper is `overflow-x-auto` so a wide table scrolls instead of the page, and
 * an element with `overflow-x: auto` computes `overflow-y` to `auto` as well.
 * An absolutely positioned panel is therefore clipped at the row's bottom edge,
 * which nobody notices until they open the last row's menu.
 *
 * **The panel is also promoted to the top layer, the same way the toaster is -
 * see `Toast.tsx`.** `Modal` and `Drawer` are native `<dialog>`s opened with
 * `showModal()`, which puts them in the top layer; a portalled div with
 * `z-index: 9999` still renders *under* an open dialog, because nothing in the
 * normal stacking context reaches the top layer at all. `Menu` and
 * `FilterSelect` mark their panel `popover="manual"` and this hook calls
 * `showPopover()` on it, which is the other way in. `manual` rather than
 * `auto`: an `auto` popover light-dismisses on outside pointerdown on its own,
 * racing the dismiss handling this hook already does below.
 *
 * **Top layer alone is not enough while that dialog is modal.** A `<dialog>`
 * opened with `showModal()` makes every node in the document inert *except*
 * its own descendants - and a popover portalled to `<body>` is a sibling of
 * the dialog, not a descendant, so it renders in the right place, on top, and
 * does not receive a single click; whatever is visually behind it, inside the
 * dialog, gets the click instead. `panelTarget` below is the fix: when the
 * trigger lives inside an open dialog, the panel portals *into that dialog*
 * instead of `<body>`, which is what exempts it from the inertness. It still
 * needs `popover="manual"` on top of that, because the dialog itself clips
 * overflow (`.modal`'s `overflow-hidden`) and only top-layer content escapes
 * an ancestor's clipping regardless of where in the DOM it sits.
 */
export interface Popover {
  open: boolean;
  toggle: () => void;
  /** `returnFocus` when the close was a user action (Escape, choosing an item). */
  close: (returnFocus?: boolean) => void;
  rootRef: React.RefObject<HTMLDivElement | null>;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  panelRef: React.RefObject<HTMLDivElement | null>;
  /** Applied to the portalled panel. Hidden until the first measurement lands. */
  panelStyle: React.CSSProperties;
  /** Where the panel portals to - the open dialog it lives inside, if any, or `<body>`. */
  panelTarget: Element;
}

export function usePopover({
  align = "end",
  /** Widen the panel to the trigger, for a control that reads as one field. */
  matchTriggerWidth = false,
  /**
   * A ceiling on the panel, on top of the room the viewport leaves.
   *
   * The space-below budget alone is not a cap: a trigger near the top of a tall
   * window leaves eight hundred pixels under it, and a list that takes all of
   * them is a wall of options rather than a menu. Whichever is smaller wins, and
   * the list scrolls inside it.
   */
  maxHeight,
}: {
  align?: "start" | "end";
  matchTriggerWidth?: boolean;
  maxHeight?: number;
} = {}): Popover {
  const [open, setOpen] = React.useState(false);
  const [position, setPosition] = React.useState<React.CSSProperties | null>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const close = React.useCallback((returnFocus = false) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  // Measured in a layout effect so the panel is placed before the browser
  // paints. In a plain effect it appears at the top-left corner for one frame
  // and then jumps, which is very visible on a slow row.
  React.useLayoutEffect(() => {
    if (!open) return;

    // Promoted before the first measurement, not after: a `popover="manual"`
    // element is UA-styled `display: none` until shown, so `offsetHeight`
    // below would read 0 and the flip-above check would never trigger.
    // Guarded on `:popover-open` because `align`/`matchTriggerWidth` changing
    // while the panel is already open re-runs this effect, and calling
    // `showPopover()` on an already-shown popover throws.
    const el = panelRef.current;
    if (el && !el.matches(":popover-open")) el.showPopover();

    function place() {
      const anchor = triggerRef.current?.getBoundingClientRect();
      if (!anchor) return;

      const panelHeight = panelRef.current?.offsetHeight ?? 0;
      const below = window.innerHeight - anchor.bottom;
      // Flip above only when there is genuinely no room below AND more room
      // above - near the top of a short viewport, cramped-below still beats
      // hanging off the top edge.
      const flip = below < panelHeight + 16 && anchor.top > below;

      setPosition({
        position: "fixed",
        // Every axis gets an explicit value on both sides, `"auto"` for the
        // one not in use. `popover="manual"` ships a UA default of
        // `inset: 0` - leaving a side out of this object (`undefined`) drops
        // it from the inline style entirely, and that default `0` wins
        // instead, pinning the panel to the viewport corner rather than the
        // trigger it is meant to hang from.
        top: flip ? "auto" : anchor.bottom + 6,
        bottom: flip ? window.innerHeight - anchor.top + 6 : "auto",
        left: align === "start" ? anchor.left : "auto",
        right: align === "end" ? window.innerWidth - anchor.right : "auto",
        minWidth: matchTriggerWidth ? anchor.width : undefined,
        maxHeight: Math.min((flip ? anchor.top : below) - 16, maxHeight ?? Infinity),
      });
    }

    place();

    /**
     * Scrolling or resizing moves the anchor and the panel does not follow, so
     * it is dismissed rather than left pointing at nothing. `capture` is what
     * catches a scroll inside the table wrapper, since scroll events do not
     * bubble.
     *
     * **A scroll that starts inside the panel is not one of those.** The panel
     * scrolls its own option list, and capturing every scroll on the page meant
     * reaching for the seventh site in a list closed the list. It was already
     * true when the panel itself was the scroller and nobody hit it, because no
     * list was long enough; a search box and a company with forty sites is what
     * makes a long list ordinary.
     */
    const dismiss = (event: Event) => {
      const target = event.target;
      if (target instanceof Node && panelRef.current?.contains(target)) return;
      setOpen(false);
    };

    window.addEventListener("scroll", dismiss, true);
    window.addEventListener("resize", dismiss);
    return () => {
      window.removeEventListener("scroll", dismiss, true);
      window.removeEventListener("resize", dismiss);
    };
  }, [open, align, matchTriggerWidth, maxHeight]);

  React.useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") close(true);
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  // Read during render rather than in an effect: by the time `open` can even
  // become true, the trigger has already rendered and been clicked, so its
  // ref is populated from a prior commit rather than one this render is
  // making. A live query rather than something computed once and cached,
  // because the same trigger renders both inside and outside a dialog across
  // its lifetime (a row action opened before and after its table's dialog).
  const panelTarget = triggerRef.current?.closest("dialog[open]") ?? document.body;

  return {
    open,
    toggle: () => setOpen((v) => !v),
    close,
    rootRef,
    triggerRef,
    panelRef,
    panelStyle: position ?? { position: "fixed", visibility: "hidden" },
    panelTarget,
  };
}

/**
 * Move DOM focus between the options of an open panel.
 *
 * **Real focus, not `aria-activedescendant`.** The virtual-cursor pattern needs
 * an active index, `data-idx` attributes, a `scrollIntoView` effect and a
 * `roving` id on the listbox, and every one of those is a thing to keep in step
 * with a filtered list. Moving focus itself gets the scrolling, the focus ring
 * and Enter-to-choose from the browser, and it keeps the property this codebase
 * already had before these panels grew a search box: every option is a real
 * button, so Tab works and a screen reader is reading something focused rather
 * than something described.
 *
 * `delta` wraps, because a list that stops dead at the last option makes the
 * common "jump to the end" a held key rather than one press of Up.
 */
export function moveOptionFocus(panel: HTMLElement | null, delta: 1 | -1): void {
  if (!panel) return;

  const options = [...panel.querySelectorAll<HTMLButtonElement>('[role="option"]:not(:disabled)')];
  if (options.length === 0) return;

  const current = options.indexOf(document.activeElement as HTMLButtonElement);
  // -1 (focus is in the search box) steps to the first on Down and the last on
  // Up, which is what both keys mean from outside a list.
  const next = current === -1 ? (delta === 1 ? 0 : options.length - 1) : current + delta;

  options[(next + options.length) % options.length].focus();
}
